import React, { FC, useState, useRef } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from 'react-query'
import { Form } from 'antd'
import { OrganizationsAPI } from '../../../shared/api/organizations'
import { SidePanel, Toast } from '../../../shared/components'
import { ApiError } from '../../../shared/models/error'
import ErrorMessage from '../../../shared/components/ErrorMessage'
import { CreateOrganizationData, Organization } from '../../../shared/models/organization'
import EditProductsForm from './EditProductsForm'
import AddResourcesForm from './AddResourcesForm'
import InviteMembersForm from './InviteMembersForm'
import MemberList, { MemberListRef } from './MemberList'
import ResourceList from './ResourceList'
import { EditFormStrings, TierPlanStrings } from '../../../shared/strings/OrganizationsContent'
import { BottomLine, ButtonsContainer, CloseButton, FormLabel, InputName, Line, SaveButton } from './Styles'
import TierPlanList from './TierPlanList'
import EditTierPlan from './EditTierPlan'

type Props = {
  organization: Organization
  onCloseEdit: () => void
  isEditingNewOrganization: boolean
}

const OrganizationsEditForm: FC<Props> = ({ organization, onCloseEdit, isEditingNewOrganization }) => {
  const [editOrgErrorMessage, setEditOrgErrorMessage] = useState<string | undefined>()
  const [isEditOrgErrorVisible, setIsEditOrgErrorVisible] = useState(false)
  const {
    handleSubmit,
    control,
    formState: { isDirty, errors },
    reset,
  } = useForm({
    defaultValues: {
      name: organization.name,
      salesforceAccountId: organization.salesforceAccountId || '',
    },
  })
  const { mutateAsync: updateOrganization, isLoading } = useMutation<
    Organization,
    ApiError,
    CreateOrganizationData & { id: number }
  >(OrganizationsAPI.update)
  const queryClient = useQueryClient()
  const memberListRef = useRef<MemberListRef>()

  const [isShowCreateSuccess, setIsShowCreateSuccess] = useState(isEditingNewOrganization)
  const [isShowEditSuccess, setIsShowEditSuccess] = useState(false)
  const [isInviteMembersPanelOpen, setIsInviteMembersPanelOpen] = useState(false)
  const [isEditResourcesPanelOpen, setIsEditResourcePanelOpen] = useState(false)
  const [isEditProductsPanelOpen, setIsEditProductsPanelOpen] = useState(false)
  const [isEditTierOpen, setIsEditTierOpen] = useState(false)

  const hideToastNotifications = () => {
    setIsShowCreateSuccess(false)
    setIsShowEditSuccess(false)
    setIsEditOrgErrorVisible(false)
    memberListRef?.current?.hideToastNotifications()
  }

  const onSubmit = async (data: CreateOrganizationData) => {
    hideToastNotifications()

    try {
      await updateOrganization(Object.assign({ id: organization.id }, data))
      queryClient.invalidateQueries(OrganizationsAPI.queryNames.GET_ALL)
      setIsShowEditSuccess(true)
      reset(data)
    } catch (err) {
      const data = err.response?.data
      const errorDetail = data?.errors ? `: ${data?.errors[0].message}` : ''
      setEditOrgErrorMessage(data?.message ? data?.message + errorDetail : EditFormStrings.editOrganizationError)
      setIsEditOrgErrorVisible(true)
    }
  }

  const onClickInviteMembers = () => {
    hideToastNotifications()
    setIsInviteMembersPanelOpen(true)
  }

  const onClickEditResources = () => {
    hideToastNotifications()
    setIsEditResourcePanelOpen(true)
  }

  const onClickEditProducts = () => {
    hideToastNotifications()
    setIsEditProductsPanelOpen(true)
  }

  const onClickEditTierPlan = () => {
    hideToastNotifications()
    setIsEditTierOpen(true)
  }

  const closeEditResourcesPanel = () => {
    setIsEditResourcePanelOpen(false)
  }

  const closeInviteMembersPanel = () => {
    setIsInviteMembersPanelOpen(false)
  }

  const closeEditProductsPanel = () => {
    setIsEditProductsPanelOpen(false)
  }

  const closeEditTierPlan = () => {
    setIsEditTierOpen(false)
  }

  const isPersonalOrganization = organization.type === 'personal'

  return (
    <div>
      <Toast
        key="create-success"
        isVisible={isShowCreateSuccess}
        text={EditFormStrings.organizationCreated}
        textId="org-created-toast-success-js"
        onDismiss={() => setIsShowCreateSuccess(false)}
        type="success"
      />
      <Toast
        key="edit-success"
        isVisible={isShowEditSuccess}
        text={EditFormStrings.organizationSaved}
        onDismiss={() => setIsShowEditSuccess(false)}
        type="success"
      />
      <Toast
        key="create-org-error"
        isVisible={isEditOrgErrorVisible}
        text={editOrgErrorMessage}
        onDismiss={() => setIsEditOrgErrorVisible(false)}
        type="error"
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Form.Item>
          <FormLabel htmlFor="organization-name-input-js">{EditFormStrings.organizationName}</FormLabel>
          <Controller
            render={({ field }) => (
              <InputName id="organization-name-input-js" {...field} disabled={isPersonalOrganization} />
            )}
            name="name"
            control={control}
            rules={{
              required: true,
            }}
          />
          {errors.name && <ErrorMessage>{EditFormStrings.organizationNameRequired}</ErrorMessage>}
        </Form.Item>
        <Form.Item>
          <FormLabel htmlFor="salesforce-account-id">{EditFormStrings.salesforceAccountId}</FormLabel>
          <Controller
            render={({ field }) => (
              <InputName id="salesforce-account-id" {...field} disabled={isPersonalOrganization} />
            )}
            name="salesforceAccountId"
            control={control}
          />
        </Form.Item>

        {!isPersonalOrganization && (
          <>
            <Line />
            <MemberList
              ref={memberListRef}
              organizationId={organization.id}
              showListHeader
              onClickAdd={onClickInviteMembers}
              onStartAction={hideToastNotifications}
            />
            <Line />
            <ResourceList
              organizationId={organization.id}
              onClickAdd={onClickEditResources}
              onStartAction={hideToastNotifications}
            />
            {/* <Line />
            <ProductList organizationId={organization.id} onClickEdit={onClickEditProducts} /> */}
            <Line />
            <TierPlanList orgId={organization.id} openEditTierPlan={onClickEditTierPlan} />
          </>
        )}
        <BottomLine />
        <ButtonsContainer>
          <CloseButton onClick={onCloseEdit} ghost>
            {EditFormStrings.closeButton}
          </CloseButton>
          <SaveButton id="save-org-button-js" type="primary" htmlType="submit" disabled={!isDirty} loading={isLoading}>
            {EditFormStrings.saveOrganization}
          </SaveButton>
        </ButtonsContainer>
      </form>
      {isEditResourcesPanelOpen && (
        <SidePanel
          key="edit-resources"
          isOpen={isEditResourcesPanelOpen}
          title={EditFormStrings.sidePanelEditResources}
          showBackButton={true}
          backButtonId="edit-resources-back-button-js"
          onClose={closeEditResourcesPanel}
          stacked={true}
        >
          <AddResourcesForm organizationId={organization.id} onCloseAdd={closeEditResourcesPanel} />
          {/* <EditResourcesForm organization={organization} onCloseAdd={closeEditResourcesPanel} /> */}
        </SidePanel>
      )}

      <SidePanel
        key="invite-members"
        isOpen={isInviteMembersPanelOpen}
        title={EditFormStrings.sidePanelInviteMembers}
        showBackButton={true}
        backButtonId="invite-members-back-button-js"
        onClose={closeInviteMembersPanel}
        stacked={true}
      >
        <InviteMembersForm organization={organization} />
      </SidePanel>
      <SidePanel
        key="edit-products"
        isOpen={isEditProductsPanelOpen}
        title={EditFormStrings.sidePanelEditApis}
        showBackButton={true}
        backButtonId="edit-products-back-button-js"
        onClose={closeEditProductsPanel}
        stacked={true}
      >
        <EditProductsForm organization={organization} onCancelEdit={closeEditProductsPanel} />
      </SidePanel>
      <SidePanel
        key="edit-tier-plan"
        isOpen={isEditTierOpen}
        title={TierPlanStrings.editTitle}
        showBackButton={true}
        backButtonId="edit-tier-plan-back-button-js"
        onClose={closeEditTierPlan}
        stacked={true}
      >
        <EditTierPlan orgId={organization.id} onCancel={closeEditTierPlan} />
      </SidePanel>
    </div>
  )
}

export default OrganizationsEditForm
